import React, { useEffect, useState } from 'react';
import { MapPin, Plus, Trash2, Pencil, Star } from 'lucide-react';
import axios from '../api/client';
import { API } from '../api/config';

// BDD Scenario 1-A: 個人設定頁面的「運送地址管理」— lets the customer save
// shipping destinations (receiver name, contact number, delivery address)
// once, then pick one when requesting a package be shipped
// (ShippingFeeModal.tsx / PackageShippingModal.tsx), instead of typing it
// freeform every time. Exactly one address is the default once any exist.

export interface ShippingAddress {
  id: string;
  label: string | null;
  receiver_name: string | null;
  phone: string | null;
  country: string;
  address: string;
  is_default: boolean;
}

interface AddressForm {
  label: string;
  receiver_name: string;
  phone: string;
  country: string;
  address: string;
}

const emptyForm: AddressForm = { label: '', receiver_name: '', phone: '', country: '', address: '' };

// Kept in sync with backend UK_DOMESTIC_LOCATIONS (app/modules/rewards/service.py) so a
// customer picking "United Kingdom" here reliably zeroes their Bonus Reward the same way
// free-typed "UK"/"England"/etc. used to (inconsistently) before this was a dropdown.
const COUNTRY_OPTIONS = [
  'United Kingdom',
  'Taiwan',
  'Hong Kong',
  'Singapore',
  'United States',
  'Australia',
  'Canada',
  'Japan',
  'Malaysia',
  'Other',
];

const ShippingAddressBook: React.FC<{ userId: string }> = ({ userId }) => {
  const [addresses, setAddresses] = useState<ShippingAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null); // null = not editing, 'new' = adding
  const [form, setForm] = useState<AddressForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API.listShippingAddresses(userId), { headers: authHeaders() });
      setAddresses(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load saved addresses.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const startAdd = () => {
    setEditingId('new');
    setForm(emptyForm);
  };

  const startEdit = (addr: ShippingAddress) => {
    setEditingId(addr.id);
    setForm({
      label: addr.label || '',
      receiver_name: addr.receiver_name || '',
      phone: addr.phone || '',
      country: addr.country,
      address: addr.address,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const submitForm = async () => {
    if (!form.receiver_name.trim() || !form.phone.trim() || !form.country.trim() || !form.address.trim()) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        label: form.label.trim() || undefined,
        receiver_name: form.receiver_name.trim(),
        phone: form.phone.trim(),
        country: form.country.trim(),
        address: form.address.trim(),
      };
      if (editingId === 'new') {
        await axios.post(API.createShippingAddress(userId), payload, { headers: authHeaders() });
      } else if (editingId) {
        await axios.put(API.updateShippingAddress(userId, editingId), payload, { headers: authHeaders() });
      }
      setEditingId(null);
      setForm(emptyForm);
      await fetchAddresses();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save the address.');
    } finally {
      setSaving(false);
    }
  };

  const deleteAddress = async (addressId: string) => {
    if (!window.confirm('Delete this saved address?')) return;
    setDeletingId(addressId);
    setError('');
    try {
      await axios.delete(API.deleteShippingAddress(userId, addressId), { headers: authHeaders() });
      await fetchAddresses();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete the address.');
    } finally {
      setDeletingId(null);
    }
  };

  const makeDefault = async (addressId: string) => {
    setSettingDefaultId(addressId);
    setError('');
    try {
      await axios.put(API.updateShippingAddress(userId, addressId), { is_default: true }, { headers: authHeaders() });
      await fetchAddresses();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to set the default address.');
    } finally {
      setSettingDefaultId(null);
    }
  };

  return (
    <div className="section-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <MapPin size={20} className="text-primary" />
        <h3 className="card-title" style={{ margin: 0 }}>Shipping Addresses</h3>
      </div>
      <p style={{ fontSize: '13px', color: '#64748B', marginTop: '-16px', marginBottom: '20px' }}>
        Save destinations here so you can pick one instead of typing it every time you pay a shipping fee.
        One address is always your default.
      </p>

      {error && (
        <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '8px', background: '#FEF2F2', color: '#991B1B', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ fontSize: '13px', color: '#64748B' }}>Loading...</p>
      ) : (
        <div style={{ display: 'grid', gap: '10px', marginBottom: '16px' }}>
          {addresses.length === 0 && editingId !== 'new' && (
            <p style={{ fontSize: '13px', color: '#94A3B8' }}>No saved addresses yet.</p>
          )}
          {addresses.map((addr) => (
            editingId === addr.id ? (
              <AddressFormRow key={addr.id} form={form} setForm={setForm} onCancel={cancelEdit} onSave={submitForm} saving={saving} />
            ) : (
              <div key={addr.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', padding: '12px', border: `1px solid ${addr.is_default ? 'var(--accent-moss)' : 'var(--border-light)'}`, borderRadius: '6px' }}>
                <div style={{ fontSize: '13px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                    {addr.label && <span style={{ fontWeight: 600 }}>{addr.label}</span>}
                    {addr.is_default && (
                      <span className="status-pill" style={{ background: '#F0FDF4', color: '#166534', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Star size={10} fill="currentColor" /> Default
                      </span>
                    )}
                  </div>
                  <div>{addr.receiver_name} · {addr.phone}</div>
                  <div>{addr.country}</div>
                  <div style={{ color: '#64748B' }}>{addr.address}</div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  {!addr.is_default && (
                    <button className="btn btn-outline" style={{ padding: '6px 10px', fontSize: '12px' }} disabled={settingDefaultId === addr.id} onClick={() => makeDefault(addr.id)}>
                      {settingDefaultId === addr.id ? 'Setting...' : 'Set Default'}
                    </button>
                  )}
                  <button className="btn btn-outline" style={{ padding: '6px 10px' }} onClick={() => startEdit(addr)}>
                    <Pencil size={12} />
                  </button>
                  <button className="btn btn-outline" style={{ padding: '6px 10px', color: '#DC2626', borderColor: '#FCA5A5' }} disabled={deletingId === addr.id} onClick={() => deleteAddress(addr.id)}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            )
          ))}
          {editingId === 'new' && (
            <AddressFormRow form={form} setForm={setForm} onCancel={cancelEdit} onSave={submitForm} saving={saving} />
          )}
        </div>
      )}

      {editingId === null && (
        <button className="btn btn-outline" style={{ fontSize: '13px' }} onClick={startAdd}>
          <Plus size={14} style={{ marginRight: '6px' }} /> Add Address
        </button>
      )}
    </div>
  );
};

const AddressFormRow: React.FC<{
  form: AddressForm;
  setForm: (f: AddressForm) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}> = ({ form, setForm, onCancel, onSave, saving }) => {
  const knownCountry = COUNTRY_OPTIONS.includes(form.country);
  const [otherSelected, setOtherSelected] = React.useState(() => !!form.country && !knownCountry);
  const showOtherInput = otherSelected || (!!form.country && !knownCountry);

  return (
  <div style={{ display: 'grid', gap: '10px', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', background: 'var(--bg-oatmeal)' }}>
    <div className="form-group" style={{ margin: 0 }}>
      <label className="form-label" htmlFor="address-label-input">Label (optional)</label>
      <input id="address-label-input" className="form-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Home, Office" />
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
      <div className="form-group" style={{ margin: 0 }}>
        <label className="form-label" htmlFor="address-receiver-input">Receiver Name</label>
        <input id="address-receiver-input" className="form-input" value={form.receiver_name} onChange={(e) => setForm({ ...form, receiver_name: e.target.value })} placeholder="Full name" />
      </div>
      <div className="form-group" style={{ margin: 0 }}>
        <label className="form-label" htmlFor="address-phone-input">Contact Number</label>
        <input id="address-phone-input" className="form-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+44..." />
      </div>
    </div>
    <div className="form-group" style={{ margin: 0 }}>
      <label className="form-label" htmlFor="address-country-select">Country</label>
      <select
        id="address-country-select"
        className="form-input"
        value={showOtherInput ? 'Other' : form.country}
        onChange={(e) => {
          const isOther = e.target.value === 'Other';
          setOtherSelected(isOther);
          setForm({ ...form, country: isOther ? '' : e.target.value });
        }}
      >
        <option value="" disabled>Select country</option>
        {COUNTRY_OPTIONS.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
      {showOtherInput && (
        <input
          id="address-country-other-input"
          className="form-input"
          style={{ marginTop: '8px' }}
          value={form.country}
          onChange={(e) => setForm({ ...form, country: e.target.value })}
          placeholder="Enter country name"
        />
      )}
    </div>
    <div className="form-group" style={{ margin: 0 }}>
      <label className="form-label" htmlFor="address-line-input">Address</label>
      <textarea id="address-line-input" className="form-input" style={{ minHeight: '60px' }} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, city, postcode" />
    </div>
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
      <button className="btn btn-outline" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={onCancel} disabled={saving}>Cancel</button>
      <button
        className="btn btn-primary"
        style={{ padding: '6px 12px', fontSize: '12px' }}
        onClick={onSave}
        disabled={saving || !form.receiver_name.trim() || !form.phone.trim() || !form.country.trim() || !form.address.trim()}
      >
        {saving ? 'Saving...' : 'Save Address'}
      </button>
    </div>
  </div>
  );
};

export default ShippingAddressBook;
